import { useCallback, useState } from "react";
import { t } from "@/i18n-js/instance";
import { useFormContext } from "react-hook-form";
import { AddRemoveToggle } from "@circle-react-shared/uikit/AddRemoveToggle";
import { Counter } from "@circle-react-shared/uikit/Counter";
import { Typography } from "@circle-react-uikit/Typography";
import { useAddOnsSettingsContext } from "../../../../AddOns/addOnsSettingsContext";

interface AddOnItemProps {
  interval: string;
  maxPurchasableQuantity?: number;
  monthlyAmount: string;
  name: string;
  priceCurrency: string;
  step?: number;
  toggleable?: boolean;
  unitSize?: number;
}

export const AddOnItem = ({
  name,
  interval,
  monthlyAmount,
  step = 1,
  toggleable = false,
  maxPurchasableQuantity,
  priceCurrency,
  unitSize,
}: AddOnItemProps) => {
  const { register } = useFormContext();
  const { defaultValues, getAddOnPrice, updateTotalPrice } =
    useAddOnsSettingsContext();

  const [price, setPrice] = useState<number | undefined>(
    defaultValues?.[name] !== undefined
      ? getAddOnPrice(name, defaultValues?.[name])
      : undefined,
  );

  const onValueChange = (value: number): void => {
    const newPrice = updatePrice(value);
    updateTotalPrice(name, newPrice);
  };

  const updatePrice = useCallback(
    (value: number | boolean): number => {
      const newPrice = getAddOnPrice(name, value);
      setPrice(newPrice);
      return newPrice;
    },
    [getAddOnPrice, name],
  );

  const label = t(`settings.billing.add_ons.manage.${name}`);
  const description = t(
    `settings.billing.add_ons.manage.${interval}.${name}_description`,
    {
      amount: monthlyAmount,
      quantity: unitSize,
    },
  );

  return (
    <div className="border-primary flex items-center justify-between gap-4 border-b py-3">
      <input type="hidden" {...register(name)} />
      <div className="flex-1">
        <Typography.LabelSm weight="semibold">{label}</Typography.LabelSm>
        <Typography.BodySm color="text-light">{description}</Typography.BodySm>
      </div>
      <div className="flex items-center gap-4">
        {toggleable ? (
          <AddRemoveToggle name={name} onChange={onValueChange} />
        ) : (
          <Counter
            name={name}
            step={step}
            onChange={onValueChange}
            max={maxPurchasableQuantity}
          />
        )}
        <div className="w-24 text-right">
          <Typography.LabelSm weight="semibold">
            {priceCurrency + price} {t("separator_slash")} {interval.charAt(0)}
          </Typography.LabelSm>
        </div>
      </div>
    </div>
  );
};
